import { CONFIG } from './config.ts';
import { Rng } from './rng.ts';
import { TRAITS, speciesTrait } from './traits.ts';

export const STAT_NAMES = ['HP', 'Attack', 'Defense', 'Magic', 'Resist', 'Speed'] as const;

export interface Rolls {
  ivs: [number, number, number, number, number, number];
  /** Stat multiplier, CONFIG.quality.min..max, bell-shaped around 1. */
  quality: number;
  /** Index into CONFIG.bloodline (tier I..V). */
  bloodline: number;
  aura: boolean;
  trait: string;
}

export function rollBloodline(rng: Rng): number {
  const r = rng.next() * 100;
  // Check rarest first so float error can only ever favour tier I.
  let acc = 0;
  for (let i = CONFIG.bloodline.length - 1; i >= 1; i--) {
    acc += CONFIG.bloodline[i].pct;
    if (r < acc) return i;
  }
  return 0;
}

export function rollTrait(rng: Rng): string {
  const pool = TRAITS.filter((t) => t.rare === rng.oneIn(CONFIG.rareTraitOneIn));
  return rng.pick(pool).id;
}

/**
 * Every roll of a new creature. A hunt wild passes auraOneIn: Infinity (its Aura is rolled on the catch,
 * rollCatchAura); an egg rolls its own at hatch. With `species`, the trait is stored as it acts on that species
 * (shared/traits.ts ownTrait: an element trait of an element it lacks becomes its own element's); the draws are the
 * same either way.
 */
export function rollCreature(rng: Rng, opts: { auraOneIn?: number; species?: string } = {}): Rolls {
  const ivs = Array.from({ length: 6 }, () => rng.int(0, 31)) as Rolls['ivs'];
  const q = CONFIG.quality;
  let u = 0;
  for (let i = 0; i < q.draws; i++) u += rng.next();
  const quality = Math.round((q.min + (q.max - q.min) * (u / q.draws)) * 1000) / 1000;
  return {
    ivs,
    quality,
    bloodline: rollBloodline(rng),
    aura: rng.oneIn(opts.auraOneIn ?? CONFIG.auraOneIn),
    trait: opts.species ? speciesTrait(rollTrait(rng), opts.species) : rollTrait(rng),
  };
}

/** Floors on a new trainer's starter (rollStarter). */
export const STARTER_FLOOR = { iv: 14, quality: 1, bloodline: 1 } as const;

/**
 * A new trainer's starter: never a bad roll, so the first creature feels good. Each Talent stat 14-31 (about 73% on
 * average), Quality the better of two rolls and at least x1.00, Bloodline at least II (III and up at the usual odds),
 * and never an Aura. It grades B at worst and usually A. Only the floor is raised, not the top odds, and the starter
 * is bound, so a fresh account can't farm one to sell.
 */
export function rollStarter(rng: Rng): Rolls {
  const r = rollCreature(rng, { auraOneIn: Infinity });
  const again = rollCreature(rng, { auraOneIn: Infinity });
  return {
    ...r,
    ivs: r.ivs.map(() => rng.int(STARTER_FLOOR.iv, 31)) as Rolls['ivs'],
    quality: Math.max(STARTER_FLOOR.quality, r.quality, again.quality),
    bloodline: Math.max(STARTER_FLOOR.bloodline, r.bloodline),
    aura: false,
  };
}

/**
 * IV share of the maximum, 0..100. With weights (see statWeights in rating.ts)
 * each stat counts by how much it matters to that species, so 31 in a stat the
 * species never uses is worth nothing.
 */
export function ivPercent(r: Rolls, weights?: readonly number[]): number {
  return Math.round(ivShare(r, weights) * 100);
}

export function ivShare(r: Rolls, weights?: readonly number[]): number {
  const w = weights ?? [1, 1, 1, 1, 1, 1];
  const tot = w.reduce((a, b) => a + b, 0);
  return r.ivs.reduce((a, iv, i) => a + (iv / 31) * w[i], 0) / tot;
}

export function qualityPercent(r: Rolls): number {
  return Math.round(qualityShare(r) * 100);
}

export function qualityShare(r: Rolls): number {
  const q = CONFIG.quality;
  return Math.min(1, Math.max(0, (r.quality - q.min) / (q.max - q.min)));
}

/**
 * How a creature's rolls compare, 0..100: talent and quality up to 20 each,
 * bloodline up to 30 (weighted by rarity) and aura 30. rating.ts adds the
 * ascension bonus and the species' talent weights.
 */
export function rollRating(r: Rolls, weights?: readonly number[]): number {
  return Math.round(rollRatingExact(r, weights));
}

export function rollRatingExact(r: Rolls, weights?: readonly number[]): number {
  const p = CONFIG.ratingPoints;
  return p.talent * ivShare(r, weights) + p.quality * qualityShare(r) + CONFIG.bloodlineRating[r.bloodline] + (r.aura ? p.aura : 0);
}

/**
 * The Aura roll of a successful catch: 1 in `oneIn` (CONFIG.auraCatchOneIn, halved under an Aura Charm). Seeded from
 * the creature's own seed, so it replays, and an Aura at 1 in n is also an Aura at any smaller n.
 */
export function rollCatchAura(seed: number, oneIn: number): boolean {
  return new Rng((seed ^ 0x6a09e667) >>> 0).oneIn(oneIn);
}
